<template>
  <div class="creators">
    <div
      id="allCreatorsBanner"
      class="creators__top-banner container background-container mt-4"
      :class="isBannerEnabled ? 'banner' : ''"
    >
      <AdBanner v-if="isBannerEnabled" type="cookbooks" />
      <div v-else>
        <p class="font-weight-bold">Whoever you follow you’ll find</p>
        <h1>
          All your your favourite <br class="creators__container-br" />
          <span class="creators__title-background">
            <span class="px-2">social stars</span>
          </span>
        </h1>
      </div>
      <ShareButton />
    </div>

    <div class="page container mt-5">
      <Carousel
        id="allCreatorsTrending"
        class="mt-5"
        :items="trendingCreators"
        @click="handleCreatorClick"
      >
        <template #title>
          <h2>Trending creators</h2>
        </template>

        <template #itemTitle="{ item }">
          <div v-if="item" class="text-overflow">
            @{{ $get(item, 'profile.handle') }}
          </div>
        </template>

        <template #itemSubtitle="{ item }">
          <span v-if="item">{{ $get(item, 'profile.tagline') }} </span>
        </template>
      </Carousel>

      <PreviewItems
        id="allCreatorsList"
        class="mt-5"
        :items="creators"
        :total="creatorsTotal"
        :loading="newCreatorsLoading"
        :filter-value="filterValue"
        :filtered-values-loading="filteredCreatorsLoading"
        :is-nothing-found="isNothingFound"
        not-found-message="Creators not found"
        title="All creators"
        @click="handleCreatorClick"
        @onViewMoreClick="getNewCreators"
        @onFilterValueChange="handleFilterValueChange"
      />
    </div>
  </div>
</template>

<script>
import { get, isEmpty } from 'lodash';
import { mapGetters } from 'vuex';
import { DefaultFilterOptions, DefaultPageLimit } from '@/misc/constants';

import ShareButton from '@/components/Elements/ShareButton';
import PreviewItems from '@/components/PreviewItems/Index';

import { fetchCreators } from '@/services/creators';
import { fetchCreatorsTrending } from '@/services/statistics';
import AdBanner from '@/components/AdBanner/index.vue';

export default {
  name: 'AllCreators',

  components: {
    AdBanner,
    ShareButton,
    PreviewItems,
    Carousel: () =>
      import(/* webpackPrefetch: true */ '@/components/Carousel/Index')
  },

  asyncData({ $axios, error }) {
    return Promise.all([
      fetchCreatorsTrending($axios),
      fetchCreators($axios, {
        query: {
          $sort: { 'profile.handle': 1 },
          $limit: DefaultPageLimit
        }
      })
    ])
      .then((values) => {
        return {
          creators: get(values, '[1].data'),
          creatorsTotal: get(values, '[1].total'),
          trendingCreators: get(values, '[0].data')
        };
      })
      .catch(() => {
        error({ statusCode: 500 });
      });
  },

  data() {
    return {
      newCreatorsLoading: false,
      filteredCreatorsLoading: false,
      isNothingFound: false,
      filterValue: DefaultFilterOptions[0].value
    };
  },

  head: {
    title: 'Discover our creators | Found',
    meta: [
      {
        hid: 'description',
        name: 'description',
        content:
          'Found it! - Discover the top social stars and get creative with their recipes, books and more!'
      }
    ]
  },

  computed: {
    ...mapGetters({
      isBannerEnabled: 'config/isBannerEnabled'
    })
  },

  methods: {
    handleCreatorClick(creator) {
      this.$router.push({
        path: `creators/${
          this.$get(creator, 'profile.handle') ||
          this.$get(creator, 'item.profile.handle') ||
          this.$get(creator, 'id')
        }`
      });
    },
    getNewCreators() {
      this.newCreatorsLoading = true;
      const query = {
        $sort: { 'profile.handle': 1 },
        $limit: DefaultPageLimit,
        $skip: this.creators.length || 0
      };
      fetchCreators(this.$axios, {
        query: this.getCreatorsQueryWithFilter(query)
      })
        .then(({ data: newCreators, total: creatorsTotal }) => {
          this.creators = [...this.creators, ...newCreators];
          this.creatorsTotal = creatorsTotal;
        })
        .catch((err) => {
          this.$showToastMessage(
            this.$get(err, 'response.data.message'),
            'Error',
            "Can't fetch new creators"
          );
        })
        .finally(() => (this.newCreatorsLoading = false));
    },
    getCreatorsQueryWithFilter(query) {
      if (this.filterValue !== 'all') {
        query.$where = {
          'profile.handle': {
            $regex: `^${this.filterValue}`,
            $options: 'i'
          }
        };
      }
      return query;
    },
    handleFilterValueChange(value) {
      this.filterValue = value;
      this.filteredCreatorsLoading = true;
      const query = {
        $sort: { 'profile.handle': 1 },
        $limit: DefaultPageLimit
      };
      fetchCreators(this.$axios, {
        query: this.getCreatorsQueryWithFilter(query)
      })
        .then(({ data: filteredCreators, total: creatorsTotal }) => {
          this.isNothingFound = isEmpty(filteredCreators);
          this.creators = filteredCreators;
          this.creatorsTotal = creatorsTotal;
        })
        .catch((err) => {
          this.$showToastMessage(
            this.$get(err, 'response.data.message'),
            'Error',
            "Can't fetch creators"
          );
        })
        .finally(() => {
          this.filteredCreatorsLoading = false;
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.creators {
  &__top-banner {
    position: relative;
    display: flex;
    justify-content: flex-end;
    flex-direction: column;
    height: 255px;
    background-color: $primary-color;
    color: $dark-color;
    padding-left: 100px;
    padding-bottom: 35px;

    h1 {
      position: relative;
      font-family: 'PPRader-Bold', sans-serif;
      font-size: 40px;
      line-height: 46px;
      text-transform: uppercase;
    }

    @media (max-width: $screen-width-xs) {
      padding-left: 15px;
      padding-bottom: 80px;
    }
  }

  &__title-background {
    background-color: $dark-color;
    border-radius: 16px;
    height: 48px;
    position: relative;
    top: -4px;

    @media (max-width: $screen-width-xs) {
      position: absolute;
      width: 305px;
      line-height: 48px;
      top: auto;
      left: -25px;
      bottom: -46px;
      padding-left: 17px !important;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }

    span {
      position: relative;
      top: 4px;
      color: $light-grey-color;
    }
  }
}

.banner {
  width: 100%;
  padding: 0;
  background-color: transparent;
  max-height: unset;
  height: unset;

  @media (max-width: $screen-width-xs) {
    height: unset;
  }
}

.mac-os {
  .creators__title-background {
    span {
      top: 0;
    }
  }
}
</style>
